'use client';

import classNames from 'classnames';
import { Field, Form, Formik } from 'formik';
import { useRouter } from 'next/navigation';
import qs from 'query-string';
import { FC, ReactNode, useCallback, useState } from 'react';

import { AUSTRALIAN_STATES, AustralianState, TRADE_IN_FORM_PATH } from 'src/constants';
import { BadgeIllustration } from 'src/general/components/BadgeIllustration/BadgeIllustration';
import { BADGE } from 'src/general/components/BadgeIllustration/BadgeIllustration.type';
import { Button } from 'src/general/components/Button/Button';
import { RecaptchaNotice } from 'src/general/components/RecaptchaNotice/RecaptchaNotice';
import { barlowCondensedFont } from 'src/general/helpers/fontsBarlow';
import { IconCaretDown } from 'src/general/Icons/IconCaretDown';
import { RegoValidation } from 'src/hygiene/components/TradeInEnquiry/TradeInEnquiry.types';
import { pushToDataLayer } from 'src/utils/pushToDataLayer';
import styles from './TradeIn.module.scss';

export interface TradeInFormData {
  rego: string;
  state: AustralianState;
}

interface Props {
  sku: string;
  microcopy: {
    header: ReactNode;
    description: ReactNode;
    submitLabel: ReactNode;
  };
}

export const TradeIn: FC<Props> = ({ sku, microcopy }) => {
  const [submissionAttempted, setSubmissionAttempted] = useState(false);
  const [userInteractedWithForm, setUserInteractedWithForm] = useState(false);
  const router = useRouter();

  const onInteract = useCallback(() => {
    if (!userInteractedWithForm) {
      pushToDataLayer({
        event: 'pdp_interaction',
        type: 'text_box',
        value: 'trade_in',
      });
      setUserInteractedWithForm(true);
    }
  }, [userInteractedWithForm, sku]);

  return (
    <section className={styles.root}>
      <div className={styles.container}>
        <div className={styles.imgContainer}>
          <BadgeIllustration icon={BADGE.TRADE_IN} background="none" shape="square" className={styles.icon} />
        </div>
        <div className={styles.content}>
          <p className="c-fs-body1 c-fw-bold">{microcopy.header}</p>
          <p className="c-fs-caption">{microcopy.description}</p>
        </div>
      </div>
      <div className={styles.rightContainer}>
        <Formik
          initialValues={{
            rego: '',
            state: 'NSW',
          }}
          onSubmit={(tradeInSearchData) => {
            pushToDataLayer({ event: 'pdp_trade_in_start' });
            router.push(`${TRADE_IN_FORM_PATH}?${qs.stringify({ ...tradeInSearchData, sku, searchType: 'byRego' })}`);
          }}
          validateOnBlur={false}
          validateOnMount={false}
          validationSchema={RegoValidation}
          validateOnChange={submissionAttempted}
        >
          {({ errors, touched, isSubmitting }) => (
            <Form className={styles.formWrapper}>
              <div className={classNames(styles.formContainer)}>
                <Field
                  type="text"
                  name="rego"
                  className={classNames(styles.input, barlowCondensedFont.variable)}
                  placeholder="TYPE REGO"
                  onClick={onInteract}
                />
                <Field as="select" name="state" className={styles.select} onClick={onInteract}>
                  {AUSTRALIAN_STATES.map((state) => (
                    <option key={state} value={state}>
                      {state}
                    </option>
                  ))}
                </Field>
                <IconCaretDown className={styles.iconCaret} />
              </div>
              {errors.rego && touched.rego ? (
                <div className={classNames(styles.error, 'c-fs-caption')}>{errors.rego}</div>
              ) : null}
              <Button
                type="submit"
                isLoading={isSubmitting}
                className={styles.button}
                onClick={() => setSubmissionAttempted(true)}
              >
                {microcopy.submitLabel}
              </Button>
            </Form>
          )}
        </Formik>
      </div>
      <RecaptchaNotice className={styles.recaptchaNotice} />
    </section>
  );
};
